import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from './Button';
import { CircularProgress } from './CircularProgress';
import { Icon, LoadingIcon } from './Icon';
import { Stack } from './Stack';
import { Heading, Text } from './Typography';

const MAX_PANEL_CONTENT_HEIGHT = 500;

export type ProgressStatus = 'running' | 'success' | 'error' | 'canceled';

export type ProgressJob = {
  title: string; // e.g., "Preparing download"
  detail?: string; // "Downloading 10 charts"
  status: ProgressStatus;
  progress?: number; // 0–100
  cancelable?: boolean;
  closeable?: boolean;
  errorMessage?: string;
};

export function useProgressPanelController(opts?: {
  autoHideOnSuccess?: boolean;
  autoHideMs?: number;
}) {
  const [job, setJob] = useState<ProgressJob | null>(null);
  const autoHideTimer = useRef<number | null>(null);
  const autoMs = opts?.autoHideMs ?? 2500;
  const autoHideOnSuccess = opts?.autoHideOnSuccess ?? false;

  const clearAutoHideTimeout = () => {
    if (autoHideTimer.current) {
      clearTimeout(autoHideTimer.current);
      autoHideTimer.current = null;
    }
  };

  const start = (j: Omit<ProgressJob, 'status'> & { status?: 'running' }) => {
    clearAutoHideTimeout();
    setJob({ status: 'running', cancelable: false, closeable: false, ...j });
  };

  const progress = (n: number, detail?: string) =>
    setJob((prev) =>
      prev ? { ...prev, progress: n, detail: detail ?? prev.detail } : prev,
    );

  const succeed = (title: string, detail?: string) => {
    clearAutoHideTimeout();
    setJob((prev) =>
      prev
        ? { ...prev, status: 'success', title, detail, closeable: true }
        : prev,
    );
    if (autoHideOnSuccess) {
      autoHideTimer.current = window.setTimeout(() => setJob(null), autoMs);
    }
  };

  const fail = (title: string, errorMessage: string) => {
    clearAutoHideTimeout();
    setJob({
      status: 'error',
      title,
      errorMessage,
      closeable: true,
    });
  };

  const cancel = () => {
    clearAutoHideTimeout();
    setJob(null);
  };

  const hide = () => {
    clearAutoHideTimeout();
    setJob(null);
  };

  useEffect(() => () => clearAutoHideTimeout(), []);

  return { job, start, progress, succeed, fail, cancel, hide };
}

export const ProgressPanel = ({
  job,
  onCancel,
  onClose,
  children,
}: {
  job: ProgressJob | null;
  onCancel?: () => void;
  onClose?: () => void;
  children?: React.ReactNode;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const ref = useRef<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | string>(
    MAX_PANEL_CONTENT_HEIGHT,
  );

  // update the max height of the panel based on the content to keep a smooth
  // transition when collapsing/expanding
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const updateHeight = () => {
      setMaxHeight(Math.min(el.scrollHeight, MAX_PANEL_CONTENT_HEIGHT));
    };

    // initial update
    updateHeight();

    // guard if ResizeObserver isn't available (older browsers, SSR)
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      // utilize RAF to avoid layout thrashing
      requestAnimationFrame(updateHeight);
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [job]);

  // expand the panel when the job is completed
  useEffect(() => {
    if (!job?.closeable || !isCollapsed) return;

    const el = ref.current;
    if (!el) return;

    let frame: number;

    const expandWhenReady = () => {
      // content is ready once it has non-zero height
      if (el.scrollHeight > 0) {
        setIsCollapsed(false);
      } else {
        frame = requestAnimationFrame(expandWhenReady);
      }
    };

    frame = requestAnimationFrame(expandWhenReady);

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [job?.closeable, isCollapsed]);

  const closePanel = () => {
    // reset to expanded state
    setMaxHeight(MAX_PANEL_CONTENT_HEIGHT);
    setIsCollapsed(false);

    onClose?.();
  };

  if (!job) return null;

  const panel = (
    <div className="fixed right-8 bottom-8 z-1000 w-[395px]">
      <Stack className="rounded-lg border border-border-primary bg-bg-primary px-5 py-6 shadow-lg">
        <Stack justify="between" gap={2} direction="horizontal">
          <Heading size="2xl">{job.title}</Heading>
          {job.closeable ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={closePanel}
              aria-label="Dismiss"
            >
              <Icon name="X" size="auto" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
            >
              <Icon
                name={isCollapsed ? 'ChevronDown' : 'ChevronUp'}
                size="auto"
              />
            </Button>
          )}
        </Stack>
        <div
          style={{ maxHeight: isCollapsed ? 0 : maxHeight }}
          className={`w-full overflow-hidden transition-all duration-300 ease-out ${
            isCollapsed ? 'opacity-0' : 'mt-5 opacity-100'
          }`}
        >
          <Stack ref={ref} gap={5}>
            <div className="w-full border-t border-border-primary" />
            <div className="w-full">
              {job.status === 'running' && (
                <Stack gap={3} role="status" direction="horizontal">
                  <div className="grow">
                    {job.detail && <Text>{job.detail}</Text>}
                  </div>
                  {typeof job.progress === 'number' ? (
                    <CircularProgress value={job.progress} strokeWidth={4} />
                  ) : (
                    <LoadingIcon size="xl" />
                  )}
                </Stack>
              )}

              {job.status === 'success' && (
                <Stack gap={3} role="status" direction="horizontal">
                  <Icon name="Check" size="xl" />
                  {job.detail && <Text>{job.detail}</Text>}
                </Stack>
              )}

              {job.status === 'error' && (
                <Stack
                  gap={3}
                  role="alert"
                  className="rounded-lg border border-border-negative-subtle bg-bg-negative-subtle p-3 text-text-negative-subtle"
                  direction="horizontal"
                >
                  <Icon name="AlertCircle" />
                  {job.errorMessage && (
                    <Text color="inherit">{job.errorMessage}</Text>
                  )}
                </Stack>
              )}
            </div>

            {children && (
              <>
                <div className="w-full border-t border-border-primary" />
                <div className="w-full">{children}</div>
              </>
            )}
            {job.cancelable && (
              <>
                <div className="w-full border-t border-border-primary" />
                <div className="w-full">
                  <Button variant="outline" size="sm" onClick={onCancel}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </Stack>
        </div>
      </Stack>
    </div>
  );

  return createPortal(panel, document.body);
};
